import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import StatusHeader from 'components/common/StatusHeader';
import UseToastComponent from 'components/common/UseToast';
import BarGuideMenuFeature from 'features/BarGuideInfoMenu';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { partition } from 'lodash';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Form, ModalProps, Table } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { barInfoListActions, selectBarInfoList } from '../barInfoSlice';
import ButtonCustom from '../components/ButtonCustom';
import Confirmation from '../components/ConfirmDialog';
import { BAR_TYPE, REGULAR_BAR } from '../constants';
import { actionType } from '../models/ActionType';
import { BarInfo } from '../models/IBarGuideInfo';
import { ButtonRef } from '../models/IButtonRef';
import BarGuideDetail from './BarGuideDetail';
import './BarGuideList.scss';

const BarGuideList: React.FC<ModalProps> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // Get bar guide data from redux store
  const barInfoListSelector = useAppSelector(selectBarInfoList);
  // state
  const [barInfoList, setBarInfoList] = useState<Array<BarInfo>>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isOpenBarDetailModal, setIsOpenBarDetailModal] = useState(false);
  const [isBarGuideMenuOpen, setIsBarGuideMenuOpen] = useState(false);

  //type
  const action = useRef<actionType>();

  // button ref
  const buttonDeleteRef = useRef<ButtonRef>(null);
  const buttonDetailRef = useRef<ButtonRef>(null);
  const buttonRestoreRef = useRef<ButtonRef>(null);
  const buttonAddNewRef = useRef<ButtonRef>(null);
  const buttonTargetMenuRefs = useRef<Array<ButtonRef>>([]);

  //ref
  const refCheckedBarIds = useRef<Array<number>>([]);
  const refRestoreCheckedBarId = useRef<number>(0);
  const refCheckBoxes = useRef<Array<HTMLInputElement>>([]);
  const refSelectedBarInfo = useRef<BarInfo>();
  const refTargetBarId = useRef<number>(0);
  const refTargetBarName = useRef<string>('');

  useEffect(() => {
    if (barInfoListSelector.length === 0) {
      dispatch(barInfoListActions.fetchListOfBarInfo());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBarInfoList(barInfoListSelector);
    if (refCheckedBarIds.current.length > 0 && barInfoListSelector.length > 0 && action.current) {
      if (action.current === 'Update') {
        const selectedBar = barInfoListSelector.find((obj) => obj.id === refCheckedBarIds.current[0]);
        if (selectedBar) {
          refSelectedBarInfo.current = selectedBar;
        }
      } else if (action.current === 'Delete' || action.current === 'Restore') {
        disableEnableButton();
        uncheckAllCheckBoxes();
      }
    }
  }, [barInfoListSelector]);

  const getBarsInArrayIds = (barList: BarInfo[], ids: number[], isDeleted: boolean) => {
    const items = barList.filter((item) => ids.includes(item.id));
    const inclusionCondition = (item: BarInfo) => item.delete_flg === isDeleted;
    return partition(items, inclusionCondition);
  };

  const handleCheckedBarInfo = (event: ChangeEvent<HTMLInputElement>) => {
    const id = event.currentTarget.id;
    const isChecked = event.currentTarget.checked;
    let updatedIds = [...refCheckedBarIds.current];

    if (isChecked && !updatedIds.includes(parseInt(id))) {
      updatedIds.push(parseInt(id));
    } else if (!isChecked) {
      const index = updatedIds.indexOf(parseInt(id));
      if (index !== -1) {
        updatedIds = updatedIds.filter((_, idx) => idx !== index);
      }
    }
    const [nonDeletedBarsByIds, deletedBarsByIds] = getBarsInArrayIds(barInfoList, updatedIds, false);
    disableEnableButton(nonDeletedBarsByIds, deletedBarsByIds);
    refCheckedBarIds.current = updatedIds;
  };

  const handleBtnRestoreCheckedBarInfo = (event: ChangeEvent<HTMLInputElement>) => {
    refRestoreCheckedBarId.current = parseInt(event.currentTarget.id);
    handleCheckedBarInfo(event);
    handleConfirmRestore();
    event.currentTarget.blur();
  };

  const handleAction = () => {
    if (refCheckedBarIds.current.length > 0) {
      const isRestore = action.current === 'Restore';
      const [filteredBars] = getBarsInArrayIds(barInfoList, refCheckedBarIds.current, isRestore);
      let arrayOfIds = filteredBars.map((item) => item.id).sort();
      if (arrayOfIds.length > 0) {
        const actionFunc = isRestore ? barInfoListActions.restoreBarInfo : barInfoListActions.deleteBarInfo;
        dispatch(actionFunc(arrayOfIds));
      }
    }
  };

  const handleConfirmDelete = () => {
    if (refCheckedBarIds.current.length > 0) {
      action.current = 'Delete';
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmRestore = () => {
    if (refCheckedBarIds.current.length > 0) {
      action.current = 'Restore';
      setShowConfirmDialog(true);
    }
  };

  const handleUncheckedCheckbox = () => {
    const crrCheckedBox = refCheckBoxes.current[refRestoreCheckedBarId.current];
    if (crrCheckedBox && crrCheckedBox.checked) {
      crrCheckedBox.click();
      crrCheckedBox.blur();
    }
    refRestoreCheckedBarId.current = 0;
  };

  const openBarDetailModal = (act: actionType) => {
    action.current = act;
    if (act === 'Add') {
      refSelectedBarInfo.current = undefined;
      buttonAddNewRef.current?.blur();
      setIsOpenBarDetailModal(true);
    } else if (act === 'Update') {
      const nonDeletedBarsByIds = barInfoList.filter(
        (item) => refCheckedBarIds.current.includes(item.id) && item.delete_flg === false
      );
      if (nonDeletedBarsByIds.length === 1) {
        refSelectedBarInfo.current = nonDeletedBarsByIds[0];
        setIsOpenBarDetailModal(true);
      }
      buttonDetailRef.current?.blur();
    }
  };

  const handleOnCloseBarDetailModal = useCallback(() => {
    setIsOpenBarDetailModal(false);
    action.current = undefined;
  }, []);

  const handleOpenBarGuideMenu = (barId: number, barName: string) => {
    refTargetBarId.current = barId;
    refTargetBarName.current = barName;
    setIsBarGuideMenuOpen(true);
    buttonTargetMenuRefs.current[barId]?.blur();
  };

  const handleCloseBarGuideMenu = useCallback(() => {
    setIsBarGuideMenuOpen(false);
    refTargetBarId.current = 0;
    refTargetBarName.current = '';
  }, []);

  const disableEnableButton = (nonDeletedBarsByIds: BarInfo[] = [], deletedBarsByIds: BarInfo[] = []) => {
    if (nonDeletedBarsByIds.length === 1) {
      buttonDetailRef.current?.enable();
    } else {
      buttonDetailRef.current?.disable();
    }
    if (nonDeletedBarsByIds.length > 0) {
      buttonDeleteRef.current?.enable();
    } else {
      buttonDeleteRef.current?.disable();
    }
    if (deletedBarsByIds.length > 0) {
      buttonRestoreRef.current?.enable();
    } else {
      buttonRestoreRef.current?.disable();
    }
  };

  const uncheckAllCheckBoxes = () => {
    refCheckBoxes.current.forEach((checkbox) => {
      checkbox.checked = false;
    });
    refCheckBoxes.current = [];
    refCheckedBarIds.current = [];
  };

  return (
    <>
      {showConfirmDialog && (
        <Confirmation
          okLabel={t('BarListTranslationFeature.DialogConfirm.btn_save')}
          cancelLabel={t('BarListTranslationFeature.DialogConfirm.btn_cancel')}
          title={t('BarListTranslationFeature.DialogConfirm.title')}
          confirmationMessage={
            action.current === 'Delete'
              ? t('BarListTranslationFeature.DialogConfirm.message_delete')
              : t('BarListTranslationFeature.DialogConfirm.message_restore')
          }
          onHide={() => {
            if (action.current === 'Restore') {
              handleUncheckedCheckbox();
            }
            setShowConfirmDialog(false);
          }}
          enableEscape={false}
          proceed={() => {
            if (action.current) {
              handleAction();
              if (action.current === 'Restore') {
                //unchecked after restore success or failed
                handleUncheckedCheckbox();
              }
            }
            setShowConfirmDialog(false); // Close the dialog after action
          }}
        />
      )}

      {isOpenBarDetailModal && (
        <BarGuideDetail
          action={action.current}
          onCloseModal={handleOnCloseBarDetailModal}
          barInfo={refSelectedBarInfo.current}
        />
      )}
      {isBarGuideMenuOpen && (
        <BarGuideMenuFeature
          barInfoId={refTargetBarId.current}
          barInfoName={refTargetBarName.current}
          onClose={handleCloseBarGuideMenu}
        />
      )}
      <UseToastComponent />
      <div className="page-wrapper">
        <div className="page-header">
          <StatusHeader onAction={() => history.push('/')} />
        </div>
        <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <div className="page-content d-flex flex-column">
            <div className="d-flex justify-content-between mb-4">
              <FeatureTitleBar title={t('BarListTranslationFeature.Text.title')}></FeatureTitleBar>
              <div>
                <ButtonCustom
                  class={'btn btn--dark-green'}
                  text={t('BarListTranslationFeature.Button.add_new')}
                  ref={buttonAddNewRef}
                  proceed={() => {
                    openBarDetailModal('Add');
                  }}
                ></ButtonCustom>
              </div>
            </div>
            <div className="mb-2 d-flex justify-content-end">
              <ButtonCustom
                class={'btn btn-primary me-4'}
                text={t('BarListTranslationFeature.Button.detail')}
                ref={buttonDetailRef}
                disabled={true}
                proceed={() => {
                  openBarDetailModal('Update');
                }}
              ></ButtonCustom>
              <ButtonCustom
                class={'btn btn--red'}
                proceed={handleConfirmDelete}
                text={t('BarListTranslationFeature.Button.delete')}
                ref={buttonDeleteRef}
                disabled={true}
              ></ButtonCustom>
            </div>

            <div className="sticky-table flex-grow-1 flex-shrink-1">
              <Table hover>
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>{t('BarListTranslationFeature.Table.select')}</th>
                    <th style={{ width: '10%' }}>{t('BarListTranslationFeature.Table.bar_guide_code')}</th>
                    <th style={{ width: '25%' }}>{t('BarListTranslationFeature.Table.bar_guide_name')}</th>
                    <th style={{ width: '10%' }}>{t('BarListTranslationFeature.Table.bar_guide_type')}</th>
                    <th style={{ width: '30%' }}>{t('BarListTranslationFeature.Table.remark')}</th>
                    <th style={{ width: '10%' }}>{t('BarListTranslationFeature.Table.target_menu')}</th>
                  </tr>
                </thead>
                <tbody>
                  {barInfoList &&
                    barInfoList.map((bar, index) => {
                      const names = bar.name.split('／');
                      return (
                        <tr
                          key={index}
                          className={
                            bar.delete_flg ? 'text-center align-middle deleted_row' : 'align-middle text-center'
                          }
                        >
                          <td>
                            {bar.delete_flg ? (
                              <>
                                <Form.Check
                                  style={{ display: 'none' }}
                                  id={bar.id.toString()}
                                  defaultChecked={false}
                                  onChange={handleBtnRestoreCheckedBarInfo}
                                  ref={(element: HTMLInputElement) => {
                                    refCheckBoxes.current[bar.id] = element;
                                  }}
                                />
                                <Form.Label className="btn btn-sm btn--orange mb-0" htmlFor={bar.id.toString()}>
                                  {t('BarListTranslationFeature.Button.restore')}
                                </Form.Label>
                              </>
                            ) : (
                              <Form.Check
                                aria-label="option"
                                id={bar.id.toString()}
                                defaultChecked={false}
                                style={{ fontSize: '1.5rem' }}
                                onChange={handleCheckedBarInfo}
                                ref={(element: HTMLInputElement) => {
                                  refCheckBoxes.current[bar.id] = element;
                                }}
                              />
                            )}
                          </td>
                          <td>{bar.code}</td>
                          <td>
                            {names.map((str, idx) => (
                              <div key={idx}>
                                {idx > 0 && '／'} {str}
                              </div>
                            ))}
                          </td>
                          <td>{BAR_TYPE[bar.type]}</td>
                          <td>{bar.remarks}</td>
                          <td>
                            {bar.type ? (
                              BAR_TYPE[REGULAR_BAR]
                            ) : (
                              <ButtonCustom
                                class={'btn btn-primary'}
                                text={t('BarListTranslationFeature.Button.target_menu')}
                                proceed={() => {
                                  if (!bar.delete_flg) {
                                    handleOpenBarGuideMenu(bar.id, bar.name);
                                  } else {
                                    return void 0;
                                  }
                                }}
                                ref={(element: ButtonRef) => {
                                  buttonTargetMenuRefs.current[bar.id] = element;
                                }}
                                disabled={bar.delete_flg}
                              ></ButtonCustom>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default BarGuideList;
